import type { DefaultTheme } from 'styled-components';
import { useTheme } from '@wistia/vhs';

export type ThemeMode = Omit<
  DefaultTheme,
  'breakpoint' | 'color' | 'duration' | 'easing' | 'font' | 'shadow' | 'spacing' | 'zIndex'
>;

export const useColorThemes = (): [ThemeMode, ThemeMode] => {
  const { color } = useTheme();
  const dark300 = '#535A70';
  const lightGrey = '#ebebed';
  const white = '#fff';
  const black = '#000';
  const almostBlack = '#232631';
  const dark100 = '#919AB4';
  const dark200 = '#747C93';
  const dark400 = '#3A4050';
  const dark500 = '#232631';
  const transparent = 'transparent';

  const lightMode = {
    themeModes: {
      type: 'dark',
      background: color.brandBlue100,
      inputTextColor: color.grey900,
      iconFill: color.brandBlue500,
      toneIconFill: color.grey900,
      activeToneIconFill: color.brandBlue500,
      selectedIconFill: color.brandBlue500,
      disabledIconFill: color.grey200,
      selectedIconBackground: color.brandBlue100,
      disabledIconBackground: color.grey100,
      teleprompterIconFill: color.grey100,
      containerBackground: white,
      borderColor: color.grey200,
      selectedButtonBorderColor: color.brandBlue300,
      settingsSelectBorderColor: color.grey200,
      optionsBorderColor: color.grey200,
      iconStroke: color.grey200,
      buttonIconFill: black,
      preRecordTeleprompterIconsFill: color.brandBlue500,
      modalBackgroundHighlight: color.blue100,
      toneBorderBg: lightGrey,
      activeToneBorderBg: color.brandBlue500,
      startRecordingButtonTextColor: white,
      captionTextColor: color.grey600,
      toneOptionContainer: '#f8f8f9',
      activeToneOptionContainer: '#E2EFFD',
      selectedLabelTextColor: color.brandBlue500,
      selectedOptionBg: color.brandBlue100,
      linkTextColor: color.brandBlue500,
      teleprompterHeaderIconFill: '#1F1F22',
      teleprompterBg: lightGrey,
      teleprompterHighlightColor: '#c7e7ff',
      teleprompterButtonColor: color.grey300,
      tabBarColor: color.grey900,
      tabBarBorderColor: color.grey300,
      tabBarUnderline: color.brandBlue500,
      activeTab: color.brandBlue500,
      hoverTab: color.brandBlue500,
      selectableProjectContainerBgColor: white,
      hoveredProjectBgColor: color.grey200,
      selectedProjectBgColor: color.brandBlue100,
      selectedProjectBorderColor: color.brandBlue600,
      teleprompterTextColor: dark400,
      hoveredProjectBorderColor: color.grey200,
      recordingControlsBg: white,
      recordingControlsDefaultColor: color.grey900,
      recordingControlsDividerColor: color.grey300,
      recordingControlsActiveColor: color.grey900,
      recordingControlsPauseIconBgActiveColor: color.green100,
      recordingControlsPauseIconActiveColor: color.grey900,
      recordingControlsTeleprompterIconActiveColor: color.grey900,
      recordingControlsTeleprompterIconBgActiveColor: color.brandBlue100,
      recordingControlsStartIconBg: color.pink200,
      recordingControlsHoverBg: color.grey200,
      recordingControlsStartIconHoverBg: color.pink300,
      recordingControlsRestartIconHoverBg: color.brandBlue200,
      recordingControlsRestartIconSelectedBg: color.brandBlue100,
      recordingControlsPauseIconHoverColor: color.green500,
      recordingControlsRestartIconHoverColor: color.grey900,
      recordingControlsInactiveColor: color.grey300,
      recordingControlsInactiveTextColor: white,
      takeManagerControlsIconActiveColor: color.grey900,
      takeManagerStartIconFill: color.pink500,
      takeManagerStartIconBg: color.pink200,
      recordingControlsDisabledIconBg: white,
      recordingControlsSelectedIconBg: color.brandBlue500,
      recordingControlsSelectedIconFill: white,
      uploadStateSavedBg: color.green100,
      uploadStateErrorBg: color.red100,
      uploadErrorModalTableHeaderColor: color.grey100,
      uploadErrorModalTableHeaderBorderColor: color.grey200,
      cardBorderColor: color.grey400,
      checkboxHoverColor: color.grey400,
      transcriptControlsBg: white,
      activeIconFill: white,
      toneContainerBg: white,
      toneButtonBg: white,
      toneButtonTextColor: color.grey900,
      hoveredToneButtonBg: color.grey200,
      transcriptContainerBg: 'rgba(255 255 255 / 60%)',
      portalTranscriptContainerBg: color.grey200,
      emptyTranscriptHighlightBg: color.grey200,
      transcriptHighlightBg: 'rgba(204 213 255 / 50%)',
      emptyTranscriptTextColor: color.grey400,
      transcriptHeaderIconFill: color.grey900,
      resizeIconFill: color.grey1100,
    },
  };

  const darkMode = {
    themeModes: {
      type: 'light',
      background: dark400,
      textColor: white,
      inputTextColor: white,
      buttonTextColor: almostBlack,
      secondaryButtonTextColor: white,
      dangerousButtonTextColor: dark500,
      flyOutButtonTextColor: white,
      buttonBackgroundColor: color.brandBlue300,
      secondaryButtonBackgroundColor: transparent,
      dangerousButtonBackgroundColor: color.red500,
      flyMenuButtonBackgroundColor: almostBlack,
      flyMenuButtonBorderColor: almostBlack,
      hoveredFlyMenuButtonTextColor: '#242732',
      disabledButtonBorderColor: dark300,
      disabledButtonBgColor: dark300,
      disabledSecondaryButtonBorderColor: dark300,
      selectedButtonBorderColor: color.brandBlue300,
      settingsSelectBorderColor: dark100,
      hoveredButtonBorderColor: color.brandBlue400,
      hoveredButtonBackgroundColor: color.brandBlue400,
      hoveredSecondaryButtonBackgroundColor: color.grey200,
      hoveredDangerousButtonBorderColor: color.red600,
      buttonBorderColor: color.brandBlue300,
      secondaryButtonBorderColor: white,
      dangerousButtonBorderColor: color.red500,
      hoveredSecondaryButtonBorderColor: color.brandBlue300,
      hoveredSecondaryButtonTextColor: color.brandBlue300,
      iconFill: color.brandBlue300,
      buttonIconFill: white,
      toneIconFill: white,
      activeToneIconFill: dark400,
      selectedIconFill: almostBlack,
      disabledIconFill: color.grey100,
      selectedIconBackground: color.brandBlue300,
      disabledIconBackground: color.grey300,
      teleprompterIconFill: color.brandBlue200,
      containerBackground: almostBlack,
      borderColor: dark100,
      optionsBorderColor: dark100,
      iconStroke: dark100,
      preRecordTeleprompterIconsFill: color.brandBlue300,
      modalBackground: dark300,
      modalBackgroundHighlight: '#515A70',
      teleprompterBorderBackground: almostBlack,
      startRecordingButtonTextColor: almostBlack,
      captionTextColor: '#8D94A8',
      toneBorderBg: '#656D87',
      activeToneBorderBg: color.brandBlue500,
      transparentBg: transparent,
      transparentBgBorderColor: '#656D87',
      toneOptionContainer: dark300,
      activeToneOptionContainer: color.brandBlue300,
      selectedLabelTextColor: color.brandBlue300,
      progressBarColor: '#D9D9D9',
      radioAccentColor: color.brandBlue300,
      progressBarMeterBgColor: dark300,
      selectedOptionBg: '#37415A',
      linkTextColor: white,
      teleprompterHeaderIconFill: white,
      teleprompterBg: dark400,
      teleprompterTextColor: white,
      teleprompterHighlightColor: '#747D99',
      teleprompterButtonColor: dark400,
      teleprompterButtonLabelColor: white,
      teleprompterScrollbarBgColor: dark300,
      teleprompterScrollbarThumbColor: '#919ab4',
      tabBarColor: dark100,
      tabBarBorderColor: dark100,
      tabBarUnderline: color.brandBlue300,
      activeTab: color.brandBlue300,
      hoverTab: color.brandBlue300,
      selectableProjectContainerBgColor: transparent,
      selectedProjectBgColor: '#3F4760',
      selectedProjectBorderColor: '#586487',
      hoveredProjectBgColor: '#3F4760',
      hoveredProjectBorderColor: '#586487',
      recordingControlsBg: dark500,
      recordingControlsDefaultColor: color.brandBlue300,
      recordingControlsDividerColor: dark300,
      recordingControlsActiveColor: white,
      recordingControlsPauseIconBgActiveColor: color.green800,
      recordingControlsPauseIconActiveColor: white,
      recordingControlsTeleprompterIconActiveColor: color.brandBlue300,
      recordingControlsTeleprompterIconBgActiveColor: '#39476C',
      recordingControlsStartIconBg: color.pink700,
      recordingControlsHoverBg: dark400,
      recordingControlsStartIconHoverBg: '#A82573',
      recordingControlsRestartIconHoverBg: color.brandBlue300,
      recordingControlsRestartIconSelectedBg: '#39476C',
      recordingControlsPauseIconHoverColor: dark500,
      recordingControlsRestartIconHoverColor: dark500,
      recordingControlsInactiveColor: dark300,
      recordingControlsInactiveTextColor: dark500,
      takeManagerControlsIconActiveColor: color.brandBlue300,
      takeManagerStartIconFill: color.pink500,
      takeManagerStartIconBg: color.pink700,
      recordingControlsDisabledIconBg: transparent,
      recordingControlsSelectedIconBg: color.brandBlue300,
      recordingControlsSelectedIconFill: dark500,
      uploadStateSavedBg: '#394F47',
      uploadStateErrorBg: '#574557',
      uploadErrorModalTableHeaderColor: dark400,
      uploadErrorModalTableHeaderBorderColor: dark200,
      cardBorderColor: dark200,
      checkboxHoverColor: dark200,
      transcriptControlsBg: dark300,
      activeIconFill: dark300,
      toneContainerBg: dark200,
      toneButtonBg: dark200,
      toneButtonTextColor: white,
      hoveredToneButtonBg: '#848CA3',
      transcriptContainerBg: 'rgba(0 0 0 / 0.15)',
      portalTranscriptContainerBg: dark400,
      emptyTranscriptHighlightBg: 'rgba(116 124 147 / 50%)',
      transcriptHighlightBg: 'rgba(153 171 255 / 50%)',
      emptyTranscriptTextColor: dark100,
      transcriptHeaderIconFill: dark100,
      resizeIconFill: dark100,
    },
  };

  return [lightMode, darkMode];
};
